import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { AlertTriangle, BellOff } from 'lucide-react';
import {
  Notification,
  Notification as NotificationType,
  NotificationFilter,
} from 'modules/core/domain/interfaces/supabase/notifications/types';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useIsAdmin } from 'shared/hooks/use-is-admin';

import { AppDispatch } from 'store/store';
import UDButton from 'ud-ui/components/button';
import { SkeletonPreview } from 'ud-ui/components/skeleton';
import HeaderContentCategory from 'ud-ui/components/category/components/list-wrapper/header-content';
import UDEmptyState from 'ud-ui/components/empty-state';
import Modal from 'ud-ui/components/modal';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { NotificationFilters } from 'modules/notifications/domain/interface/notification';
import { hasNewNotifications } from 'modules/notifications/domain/utils/has-new-notifications';
import { deleteNotification, fetchNotifications } from 'modules/notifications/store/actions';
import { setFilters, setOffset } from 'modules/notifications/store/notifications';
import {
  selectHasMoreNotifications,
  selectNotifications,
  selectNotificationsError,
  selectNotificationsFilters,
  selectNotificationsLoading,
  selectNotificationsTotal,
} from 'modules/notifications/store/selectors';
import NotificationComponent from '../components/notification';
import NotificationSkeleton, { NotificationSkeletonList } from '../components/notification-skeleton';
import NotificationsFilters from '../components/notifications-filters';
import { useNotificationsErrorReason } from '../hooks/use-notifications-error-reason';
import useUnreadNotifications from '../hooks/use-unread-notifications';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

const ITEMS_PER_PAGE = 10;

const NotificationsPage: React.FC = () => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useSelector(selectNotifications);
  const loading = useSelector(selectNotificationsLoading);
  const error = useSelector(selectNotificationsError);
  const total = useSelector(selectNotificationsTotal);
  const filters = useSelector(selectNotificationsFilters);
  const hasMore = useSelector(selectHasMoreNotifications);
  const errorReason = useNotificationsErrorReason(error);
  const isAdmin = useIsAdmin();
  const { info } = useOrganizer();
  const organizerId = info?.id;
  const [pendingDeleteId, setPendingDeleteId] = React.useState<number | null>(null);
  const { setHeaderContent } = useMainLayout();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const openDeleteModal = useCallback(() => setIsModalOpen(true), []);
  const closeDeleteModal = useCallback(() => setIsModalOpen(false), []);

  const getApiFilters = useCallback((): NotificationFilter => {
    return {
      type: filters.type === 'all' ? undefined : filters.type,
      userId: organizerId,
      limit: ITEMS_PER_PAGE,
      offset: filters.offset,
      sort: filters.sort,
    };
  }, [filters, organizerId]);

  useEffect(() => {
    const apiFilters = getApiFilters();
    dispatch(fetchNotifications(apiFilters));
  }, [dispatch, getApiFilters]);

  const handleFilterChange = (newFilters: NotificationFilters) => {
    dispatch(setFilters({ ...newFilters, limit: ITEMS_PER_PAGE, userId: organizerId }));
  };

  const navigate = useNavigate();

  const fetchMore = useCallback(() => {
    if (!hasMore) {
      return;
    }
    if (notifications.length < total) {
      dispatch(setOffset((filters.offset ?? 0) + ITEMS_PER_PAGE));
    }
  }, [notifications.length, total, dispatch, filters.offset, hasMore]);

  // Бесконечная прокрутка
  useEffect(() => {
    const handleScroll = () => {
      if (loading || notifications.length >= total || !hasMore) {
        return;
      }
      const scrollPosition = window.scrollY + window.innerHeight;
      const bottomPosition = document.documentElement.scrollHeight;
      if (bottomPosition - scrollPosition < 200) {
        fetchMore();
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [fetchMore, loading, notifications.length, total, hasMore]);

  // Обработчики для админа
  const handleEdit = useCallback(
    (notification: Notification) => {
      navigate(Pages.NOTIFICATIONS.builders.edit(notification.id));
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (id: number) => {
      setPendingDeleteId(id);
      openDeleteModal();
    },
    [openDeleteModal],
  );

  const handleCancelDelete = useCallback(() => {
    setPendingDeleteId(null);
    closeDeleteModal();
  }, [closeDeleteModal]);

  const handleConfirmDelete = useCallback(async () => {
    if (pendingDeleteId == null) {
      return;
    }
    if (!isAdmin) {
      return;
    }
    try {
      await dispatch(deleteNotification(pendingDeleteId)).unwrap();
      toast.success(t`Уведомление удалено`);
    } catch (error) {
      toastApiError(error, msg`Не удалось удалить уведомление`);
    } finally {
      setPendingDeleteId(null);
      closeDeleteModal();
    }
  }, [pendingDeleteId, isAdmin, dispatch, closeDeleteModal, t, toastApiError]);

  const handleCreate = useCallback(() => {
    navigate(Pages.NOTIFICATIONS.CREATE);
  }, [navigate]);

  // Хук непрочитанных: глобально помечает как прочитанные после загрузки (только на странице)
  const { markAllAsRead } = useUnreadNotifications({ autoFetch: false, markOnLoad: true });

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  // Заголовок и «Создать» — в шапке макета, как на остальных страницах списков; создавать может только админ.
  useEffect(() => {
    setHeaderContent(
      <HeaderContentCategory
        title={t`Уведомления`}
        createButtonText={isAdmin ? t`Создать` : undefined}
        createButtonOnClick={handleCreate}
      />,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent, t, isAdmin, handleCreate]);

  return (
    <>
      <div className="flex h-full w-full flex-col gap-4 p-6">
        <NotificationsFilters filters={filters} onChange={handleFilterChange} />
        {notifications.length > 0 && (
          <div className="flex justify-end">
            <UDButton variant="gray" onClick={handleMarkAllAsRead}>
              <Trans>Прочитать все</Trans>
            </UDButton>
          </div>
        )}
        <SkeletonPreview skeleton={<NotificationSkeletonList />}>
          <div className={classNames('flex flex-col gap-3', { 'flex-1': !notifications.length })}>
            {loading && !notifications.length && <NotificationSkeletonList />}
            {!loading && !notifications.length && error === null && (
              <div className="flex flex-1 flex-col items-center justify-center py-8">
                <UDEmptyState icon={<BellOff size={28} />} title={t`Уведомлений нет`} subtitle={t`Новые уведомления появятся здесь`} />
              </div>
            )}
            {error !== null && (
              <div className="flex flex-1 flex-col items-center justify-center py-8">
                <UDEmptyState icon={<AlertTriangle size={28} />} title={t`Не удалось загрузить уведомления`} subtitle={errorReason} />
              </div>
            )}
            {notifications.map((notification: NotificationType) => (
              <NotificationComponent
                key={notification.id}
                notification={notification}
                isNew={hasNewNotifications([notification])}
                allowChange={isAdmin}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
            {loading && notifications.length > 0 && <NotificationSkeleton />}

            <Modal.Confirm
              show={isModalOpen}
              title={t`Удалить уведомление?`}
              description={t`Это действие необратимо.`}
              isLoading={loading}
              onConfirm={handleConfirmDelete}
              onCancel={handleCancelDelete}
            />
          </div>
        </SkeletonPreview>
      </div>
    </>
  );
};

export default NotificationsPage;
